import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

/** V1 `public.bookings` row shape for admin lists */
export type AdminBookingRow = Record<string, unknown>;

function mergeRealtimeRow(
  prev: AdminBookingRow[],
  payload: {
    eventType: 'INSERT' | 'UPDATE' | 'DELETE';
    new: AdminBookingRow | null;
    old: AdminBookingRow | null;
  },
): AdminBookingRow[] {
  if (payload.eventType === 'DELETE' && payload.old?.id) {
    return prev.filter((r) => r.id !== payload.old?.id);
  }
  if (payload.eventType === 'INSERT' && payload.new) {
    const exists = prev.some((r) => r.id === payload.new?.id);
    if (exists) {
      return prev.map((r) =>
        r.id === payload.new?.id ? { ...r, ...payload.new } : r,
      );
    }
    return [payload.new, ...prev];
  }
  if (payload.eventType === 'UPDATE' && payload.new) {
    const id = payload.new.id;
    if (!id) return prev;
    const ix = prev.findIndex((r) => r.id === id);
    if (ix === -1) return [payload.new, ...prev];
    const next = [...prev];
    next[ix] = { ...next[ix], ...payload.new };
    return next.sort(
      (a, b) =>
        String(b.created_at ?? '').localeCompare(String(a.created_at ?? '')),
    );
  }
  return prev;
}

/**
 * Live `bookings` list for admin dashboards. Requires admin RLS + realtime publication on `bookings`.
 * Optional desktop notification when a new row is inserted (browser Notification API).
 */
export function useAdminBookingsRealtime(enabled: boolean, opts?: { notify?: boolean }) {
  const notifyNew = opts?.notify !== false;
  const [rows, setRows] = useState<AdminBookingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const { data, error: qErr } = await supabase
      .from('bookings')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(40);

    if (qErr) {
      setError(qErr.message);
      return;
    }
    setError(null);
    setRows(data || []);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setRows([]);
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      setLoading(true);
      await refresh();
      if (!cancelled) setLoading(false);
    })();

    const channel = supabase
      .channel('admin-bookings-realtime-v1')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'bookings' },
        (payload) => {
          setRows((prev) =>
            mergeRealtimeRow(prev, payload as Parameters<typeof mergeRealtimeRow>[1]),
          );

          if (
            notifyNew &&
            payload.eventType === 'INSERT' &&
            payload.new &&
            typeof window !== 'undefined' &&
            'Notification' in window &&
            Notification.permission === 'granted'
          ) {
            const n = payload.new as Record<string, unknown>;
            const ref = String(n.id ?? '').replace(/-/g, '').slice(0, 8).toUpperCase();
            try {
              new Notification('New AbleGo booking', {
                body:
                  `${String(n.customer_name ?? 'Guest')} · ${ref}\n` +
                  `${String(n.pickup_address ?? '').slice(0, 55)}…`,
                tag: String(n.id ?? ''),
                silent: false,
              });
            } catch {
              /* ignore */
            }
          }
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      void supabase.removeChannel(channel);
    };
  }, [enabled, notifyNew, refresh]);

  return { rows, loading, error, refresh };
}

/** Call once from admin shell if you want OS notifications (requires user gesture in some browsers). */
export function requestAdminBookingNotifications() {
  if (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    Notification.permission === 'default'
  ) {
    void Notification.requestPermission();
  }
}
